import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { axiosConfig } from "../../../axios/axiosConfig";

const SelectPlanPopup = ({ open, onClose, onSaved, screenRow }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchSubscriptionPlans = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: "/payment/plans" });
      const allPlans = res?.data?.response?.data || res?.data?.data || [];
      setPlans(allPlans);
    } catch (error) {
      toast.error(error?.response?.data?.response || error.message || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedPlans = async () => {
    try {
      const res = await axiosConfig({
        method: "get",
        url: `/home/screen-tag-with-plan/?screen_id=${screenRow?.id}`,
      });
      const selected = [...new Set((res?.data?.response || []).map((p) => p.toLowerCase()))];
      setSelectedPlans(selected);
    } catch (error) {
      toast.error(error?.response?.data?.response || error.message || "Could not load selected plans");
    }
  };

  useEffect(() => {
    if (open) {
      (async () => {
        await fetchSubscriptionPlans();
        await fetchSelectedPlans();
      })();
    }
  }, [open]);

  const allCodes = ["trial", ...plans.map((p) => p.plan_code.toLowerCase())];
  const allSelected = selectedPlans.length === allCodes.length;
  const someSelected = selectedPlans.length > 0 && !allSelected;

  const handleSelectAll = (e) => {
    setSelectedPlans(e.target.checked ? allCodes : []);
  };

  const handleSelect = (code) => {
    const lc = code.toLowerCase();
    setSelectedPlans((prev) =>
      prev.includes(lc) ? prev.filter((item) => item !== lc) : [...prev, lc]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const uniquePlans = [...new Set(selectedPlans.map((p) => p.toLowerCase()))];
      await axiosConfig({
        method: "post",
        url: "/home/screen-tag-with-plan/",
        data: { screen_id: screenRow?.id, plan_list: uniquePlans },
      });
      toast.success("Plans saved successfully");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "14px" } }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, background: "#04343a", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AddCircleOutlineIcon sx={{ color: "#eef2f7", fontSize: 22 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
              Select Subscription Plans
            </Typography>
            {screenRow?.name && (
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
                {screenRow.name} &mdash; {screenRow.key}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#eef2f7", "&:hover": { color: "#fff" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, background: "#eef2f7" }}>
        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#04343a" }} />
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            {/* Select All strip */}
            <Box
              sx={{
                px: 2, py: 1,
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    disabled={plans.length === 0}
                    sx={{
                      color: "#016551",
                      "&.Mui-checked": { color: "#016551" },
                      "&.MuiCheckbox-indeterminate": { color: "#016551" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                    Select All {allCodes.length > 0 && `(${allCodes.length})`}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              {selectedPlans.length > 0 && (
                <Typography sx={{ ml: "auto", fontSize: 12, color: "#016551", fontWeight: 600 }}>
                  {selectedPlans.length} selected
                </Typography>
              )}
            </Box>

            {/* Plan Cards */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {/* Trial */}
              {(() => {
                const selected = selectedPlans.includes("trial");
                return (
                  <Box
                    onClick={() => handleSelect("trial")}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.5, py: 1,
                      border: selected ? "1.5px solid #016551" : "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      background: selected ? "#f0fdf4" : "#fff",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: "#016551", background: "#f0fdf4" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => handleSelect("trial")}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0, color: "#016551", "&.Mui-checked": { color: "#016551" } }}
                    />
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.2 }}>
                        Trial
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10, fontWeight: 700,
                          color: selected ? "#016551" : "#64748b",
                          background: selected ? "#dcfce7" : "#f1f5f9",
                          borderRadius: "4px", px: 0.7, py: 0.15,
                          display: "inline-block", mt: 0.3,
                        }}
                      >
                        trial
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}

              {/* All Plans */}
              {plans.map((p) => {
                const selected = selectedPlans.includes(p.plan_code.toLowerCase());
                return (
                  <Box
                    key={p.id}
                    onClick={() => handleSelect(p.plan_code)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.5, py: 1,
                      border: selected ? "1.5px solid #016551" : "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      background: selected ? "#f0fdf4" : "#fff",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: "#016551", background: "#f0fdf4" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => handleSelect(p.plan_code)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0, color: "#016551", "&.Mui-checked": { color: "#016551" } }}
                    />
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.2 }}>
                        {p.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                        <Typography
                          sx={{
                            fontSize: 10, fontWeight: 700,
                            color: selected ? "#016551" : "#64748b",
                            background: selected ? "#dcfce7" : "#f1f5f9",
                            borderRadius: "4px", px: 0.7, py: 0.15,
                          }}
                        >
                          {p.plan_code}
                        </Typography>
                        {p.price && (
                          <Typography sx={{ fontSize: 10, color: "#64748b" }}>· ₹{p.price}</Typography>
                        )}
                        {p.interval && p.interval_unit && (
                          <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                            · {p.interval} {p.interval_unit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", gap: 1 }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving || loading || selectedPlans.length === 0}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
          sx={{
            background: "linear-gradient(135deg, #016551, #014d3d)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            px: 2.5,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            "&:disabled": { background: "#94a3b8", boxShadow: "none" },
          }}
        >
          {saving ? "Saving…" : `Save${selectedPlans.length > 0 ? ` (${selectedPlans.length})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectPlanPopup;
